<template>
  <div class="min-h-screen pb-20">
    <!-- Header -->
    <header class="bg-white shadow-sm sticky top-0 z-10">
      <div class="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">{{ $t('lists.title') }}</h1>
        </div>
        <UserMenu />
      </div>
    </header>

    <!-- Content -->
    <main class="max-w-7xl mx-auto px-4 py-6">
      <!-- Pending Invitations Banner -->
      <div
        v-if="pendingInvitations.length > 0"
        class="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="text-blue-600">📬</span>
            <span class="text-blue-900 font-medium">
              {{ $t('lists.pendingInvitations', { count: pendingInvitations.length }, pendingInvitations.length) }}
            </span>
          </div>
          <button
            @click="$router.push('/invitations')"
            class="text-blue-600 hover:text-blue-700 font-medium text-sm"
          >
            {{ $t('common.view') }}
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
        <p class="mt-4 text-gray-600">{{ $t('lists.loading') }}</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="lists.length === 0" class="text-center py-12">
        <div class="text-6xl mb-4">📝</div>
        <h2 class="text-2xl font-semibold text-gray-900 mb-2">{{ $t('lists.noListsYet') }}</h2>
        <p class="text-gray-600 mb-6">{{ $t('lists.noListsDescription') }}</p>
        <button @click="showCreateModal = true" class="btn btn-primary">
          {{ $t('lists.createList') }}
        </button>
      </div>

      <!-- Lists Grid -->
      <div v-else class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="list in lists"
          :key="list.id"
          @click="$router.push(`/lists/${list.id}`)"
          class="card hover:shadow-md transition-shadow cursor-pointer"
        >
          <div class="flex items-start justify-between mb-3">
            <h3 class="text-lg font-semibold text-gray-900 flex-1">{{ list.name }}</h3>
            <span v-if="!list.is_owner" class="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
              {{ $t('lists.shared') }}
            </span>
          </div>

          <div class="flex items-center gap-4 text-sm text-gray-600">
            <span>📦 {{ $t('lists.items', { count: list.items_count }, list.items_count) }}</span>
            <span v-if="list.shared_with_count > 0">
              👥 {{ list.shared_with_count }}
            </span>
          </div>

          <div class="mt-3 text-xs text-gray-500">
            <span v-if="!list.is_owner">
              {{ $t('common.by') }} {{ list.owner.email }}
            </span>
          </div>
        </div>
      </div>
    </main>

    <!-- FAB - Create List -->
    <button @click="showCreateModal = true" class="fab">
      +
    </button>

    <!-- Create List Modal -->
    <div
      v-if="showCreateModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      @click.self="showCreateModal = false"
    >
      <div class="bg-white rounded-xl p-6 max-w-md w-full">
        <h2 class="text-xl font-bold mb-4">{{ $t('lists.createNewList') }}</h2>
        <form @submit.prevent="handleCreateList">
          <input
            v-model="newListName"
            type="text"
            class="input mb-4"
            :placeholder="$t('lists.listNamePlaceholder')"
            required
            autofocus
          />
          <div class="flex gap-3">
            <button
              type="button"
              @click="showCreateModal = false"
              class="btn btn-secondary flex-1"
            >
              {{ $t('common.cancel') }}
            </button>
            <button
              type="submit"
              class="btn btn-primary flex-1"
              :disabled="creating"
            >
              {{ creating ? $t('lists.creating') : $t('common.create') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '../composables/useAuth'
import apiClient from '../api/client'
import UserMenu from '../components/UserMenu.vue'

const router = useRouter()
const { user } = useAuth()
const { t } = useI18n()

const lists = ref([])
const pendingInvitations = ref([])
const loading = ref(true)
const showCreateModal = ref(false)
const newListName = ref('')
const creating = ref(false)

const fetchLists = async () => {
  try {
    const response = await apiClient.get('/api/lists/')
    lists.value = response.data
  } catch (error) {
    console.error('Failed to fetch lists:', error)
  } finally {
    loading.value = false
  }
}

const fetchPendingInvitations = async () => {
  try {
    const response = await apiClient.get('/api/lists/invitations/pending')
    pendingInvitations.value = response.data
  } catch (error) {
    console.error('Failed to fetch invitations:', error)
  }
}

const handleCreateList = async () => {
  creating.value = true
  try {
    const response = await apiClient.post('/api/lists/', {
      name: newListName.value,
    })
    lists.value.unshift(response.data)
    showCreateModal.value = false
    newListName.value = ''

    router.push(`/lists/${response.data.id}`)
  } catch (error) {
    console.error('Failed to create list:', error)
    alert(t('lists.failedToCreate'))
  } finally {
    creating.value = false
  }
}

onMounted(() => {
  fetchLists()
  fetchPendingInvitations()
})
</script>
